import { IObservableArray, makeAutoObservable, observable } from "mobx";
import { AccountAutomatedReferralsApi } from "api/account-automated-referrals.api";
import { AppStore } from "store/App.store";
import { TGrsPendingLead } from "types/grs-pending-lead.type";
import { GRS_PENDING_PAGE_SIZE } from "constants/grs-pending-leads";
import {
  toGrsPendingLead,
  toGrsPendingLeadDetail,
} from "utils/grs-pending-leads.utils";

const automatedReferralsApi = new AccountAutomatedReferralsApi();

export class AccountGrsPendingLeadsStore {
  private readonly root: AppStore;
  private readonly accountId: number;
  private readonly pendingLeads: IObservableArray<TGrsPendingLead>;
  private visibleCountData: number;
  private latestFetchId: number;

  constructor(root: AppStore, accountId: number) {
    makeAutoObservable(this, {}, { autoBind: true });

    this.root = root;
    this.accountId = accountId;
    this.pendingLeads = observable.array<TGrsPendingLead>();
    this.visibleCountData = GRS_PENDING_PAGE_SIZE;
    this.latestFetchId = 0;
  }

  get pendingLeadsCount() {
    return this.pendingLeads.length;
  }

  get visiblePendingLeads() {
    return this.pendingLeads.slice(0, this.visibleCountData);
  }

  get hiddenPendingLeadsCount() {
    return Math.max(this.pendingLeadsCount - this.visibleCountData, 0);
  }

  get nextRevealCount() {
    return Math.min(this.hiddenPendingLeadsCount, GRS_PENDING_PAGE_SIZE);
  }

  private setPendingLeads(pendingLeads: TGrsPendingLead[]) {
    this.pendingLeads.replace(pendingLeads);
    this.visibleCountData = Math.min(
      Math.max(GRS_PENDING_PAGE_SIZE, this.visibleCountData),
      pendingLeads.length
    );
  }

  private discardInFlightFetch() {
    this.latestFetchId += 1;
  }

  public async fetchPendingLeadDetail(pendingLeadId: number) {
    const referral = await automatedReferralsApi.fetchReceivedReferralDetails(
      this.accountId,
      pendingLeadId
    );

    return toGrsPendingLeadDetail(referral);
  }

  private removePendingLead(pendingLeadId: number) {
    this.setPendingLeads(
      this.pendingLeads.filter(({ id }) => id !== pendingLeadId)
    );
  }

  private refreshPendingLeadsInBackground() {
    this.fetchPendingLeads().catch(() => {
      console.error("Failed to refresh GRS pending leads");
    });
  }

  public async fetchTerms() {
    return automatedReferralsApi.fetchTerms(this.accountId);
  }

  public async fetchRejectionReasons() {
    return automatedReferralsApi.fetchRejectionReasons(this.accountId);
  }

  public async acceptPendingLead(pendingLeadId: number, termsId: number) {
    const acceptedLead = await automatedReferralsApi.acceptReceivedReferral(
      this.accountId,
      pendingLeadId,
      termsId
    );

    this.removePendingLead(pendingLeadId);
    this.root.userAccountsStore.userAccountsMap[
      this.accountId
    ]?.accountLeadsStore.prependAcceptedLead(acceptedLead);
    this.refreshPendingLeadsInBackground();

    return acceptedLead;
  }

  public async rejectPendingLead(
    pendingLeadId: number,
    rejectionReasonId: number,
    notes?: string
  ) {
    await automatedReferralsApi.rejectReceivedReferral(
      this.accountId,
      pendingLeadId,
      rejectionReasonId,
      notes || undefined
    );

    this.removePendingLead(pendingLeadId);
    this.refreshPendingLeadsInBackground();
  }

  public showMore() {
    this.visibleCountData = Math.min(
      this.visibleCountData + GRS_PENDING_PAGE_SIZE,
      this.pendingLeadsCount
    );
  }

  public clearPendingLeads() {
    this.discardInFlightFetch();
    this.visibleCountData = GRS_PENDING_PAGE_SIZE;
    this.pendingLeads.replace([]);
  }

  public async fetchPendingLeads() {
    this.discardInFlightFetch();
    const fetchId = this.latestFetchId;
    const referrals = await automatedReferralsApi.fetchReceivedReferrals(
      this.accountId
    );

    if (fetchId !== this.latestFetchId) return;
    this.setPendingLeads(referrals.map(toGrsPendingLead));
  }
}
